import { useEffect, useMemo, useRef, useState } from "react"
import axios from "axios"
import toast from "react-hot-toast"

const MAX_ITEMS = 10

/** Debounced product search terhadap /product/search. Mengembalikan {id, name, image}. */
function useProductSearch() {
    const [query, setQuery] = useState("")
    const [results, setResults] = useState([])
    const [searching, setSearching] = useState(false)
    const debounceRef = useRef(null)
    const abortRef = useRef(null)

    useEffect(() => {
        if (debounceRef.current) window.clearTimeout(debounceRef.current)
        if (!query.trim()) {
            setResults([])
            setSearching(false)
            return
        }

        debounceRef.current = window.setTimeout(() => {
            if (abortRef.current) abortRef.current.abort()
            const controller = new AbortController()
            abortRef.current = controller
            setSearching(true)

            axios.get(`${process.env.REACT_APP_INVENTORY_API_URL}/product/search`, {
                params: { q: query, per_page: 15 },
                signal: controller.signal
            })
                .then((res) => setResults(res?.data?.data?.data ?? []))
                .catch((error) => { if (error?.code !== "ERR_CANCELED" && error?.name !== "CanceledError") console.error("Search failed", error) })
                .finally(() => setSearching(false))
        }, 300)

        return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current) }
    }, [query])

    return { query, setQuery, results, searching, clear: () => { setQuery(""); setResults([]) } }
}

export default function Form({ data, errors = {}, onSubmit }) {
    const isEdit = !!data

    const [productModelId, setProductModelId] = useState(data?.product_model_id ?? "")
    const [sourceName, setSourceName] = useState(data?.source_name ?? "")
    const [sourceImage, setSourceImage] = useState(data?.source_image ?? null)
    const [items, setItems] = useState(data?.items ?? [])

    const sourceSearch = useProductSearch()
    const similarSearch = useProductSearch()

    const selectedIds = useMemo(() => new Set(items.map((i) => i.similar_product_model_id)), [items])
    const isFull = items.length >= MAX_ITEMS

    const pickSource = (hit) => {
        setProductModelId(hit.id)
        setSourceName(hit.name)
        setSourceImage(hit.image ?? null)
        setItems((prev) => prev.filter((it) => it.similar_product_model_id !== hit.id))
        sourceSearch.clear()
    }

    const clearSource = () => {
        setProductModelId("")
        setSourceName("")
        setSourceImage(null)
    }

    const addItem = (hit) => {
        if (hit.id === productModelId) { toast.error("Produk sumber tidak bisa jadi produk serupa-nya sendiri."); return }
        if (selectedIds.has(hit.id)) { toast("Produk ini sudah dipilih."); return }
        if (items.length >= MAX_ITEMS) { toast.error(`Maksimal ${MAX_ITEMS} produk serupa.`); return }
        setItems([...items, { similar_product_model_id: hit.id, name: hit.name, image: hit.image ?? null, sort_order: items.length }])
    }

    const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx))

    const moveItem = (idx, dir) => {
        const target = idx + dir
        if (target < 0 || target >= items.length) return
        const next = [...items]
        const tmp = next[idx]
        next[idx] = next[target]
        next[target] = tmp
        setItems(next)
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!productModelId) { toast.error("Pilih produk sumber dulu."); return }
        if (!items.length) { toast.error("Minimal 1 produk serupa wajib dipilih."); return }
        if (items.length > MAX_ITEMS) { toast.error(`Maksimal ${MAX_ITEMS} produk serupa.`); return }

        const payloadItems = items.map((it, i) => ({ similar_product_model_id: it.similar_product_model_id, sort_order: i }))

        onSubmit({ product_model_id: productModelId, items: payloadItems })
    }

    return (
        <form onSubmit={handleSubmit} className="mt-8 space-y-8">
            <div className="md:grid md:grid-cols-12 md:gap-6">
                {/* LEFT: source */}
                <div className="space-y-4 md:col-span-5">
                    <div>
                        <label className="text-sm font-semibold text-neutral-800">Produk Sumber <span className="text-red-500">*</span></label>
                        <p className="text-[11px] text-neutral-500 mb-2">Halaman detail produk inilah yang akan menampilkan daftar produk serupa.</p>
                        {productModelId ? (
                            <div className="flex items-center gap-3 p-3 border border-neutral-200 rounded-xl">
                                <div className="flex-shrink-0 overflow-hidden w-14 h-14 rounded-lg bg-neutral-100">
                                    {sourceImage && <img src={sourceImage} alt="" className="object-cover w-full h-full" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{sourceName || "(Tanpa nama)"}</div>
                                    <div className="font-mono text-[11px] text-neutral-400 truncate">{productModelId}</div>
                                </div>
                                {!isEdit && <button type="button" onClick={clearSource} className="px-3 py-2 text-xs rounded-xl bg-neutral-200">Ganti</button>}
                            </div>
                        ) : (
                            <>
                                <input value={sourceSearch.query} onChange={(e) => sourceSearch.setQuery(e.target.value)} placeholder="Cari produk sumber..." className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                                {sourceSearch.searching && <div className="mt-1 text-xs text-neutral-500">Mencari...</div>}
                                {sourceSearch.results.length > 0 && (
                                    <div className="mt-2 overflow-auto border max-h-72 rounded-xl border-neutral-200">
                                        {sourceSearch.results.map((hit) => (
                                            <button key={hit.id} type="button" onClick={() => pickSource(hit)} className="flex items-center w-full gap-3 p-2 text-left border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50">
                                                <div className="flex-shrink-0 w-12 h-12 overflow-hidden rounded-lg bg-neutral-100">
                                                    {hit.image && <img src={hit.image} alt="" className="object-cover w-full h-full" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium truncate">{hit.name}</div>
                                                    <div className="font-mono text-[11px] text-neutral-400">{hit.id}</div>
                                                </div>
                                                <div className="text-xs font-semibold">+ Pilih</div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {errors.product_model_id && <div className="mt-1 text-xs text-red-500">{errors.product_model_id}</div>}
                            </>
                        )}
                    </div>
                    <div className="p-4 text-xs leading-relaxed border border-neutral-200 rounded-xl text-neutral-500">
                        Daftar di kanan diprioritaskan. Bila kurang dari 10, sisanya diisi otomatis (produk satu kategori, brand mirip). Produk sold-out tidak ditampilkan.
                    </div>
                </div>

                {/* RIGHT: similar picker */}
                <div className="mt-6 space-y-4 md:col-span-7 md:mt-0">
                    <div>
                        <label className="text-sm font-semibold text-neutral-800">
                            Cari produk serupa{" "}
                            <span className={isFull ? "text-red-500" : "text-neutral-400"}>({items.length}/{MAX_ITEMS})</span>
                        </label>
                        {isFull && <div className="text-[11px] text-red-500 mb-1">Sudah mencapai maksimal {MAX_ITEMS} produk. Hapus salah satu untuk mengganti.</div>}
                        <div className="relative">
                            <input value={similarSearch.query} onChange={(e) => similarSearch.setQuery(e.target.value)} placeholder="Ketik nama produk..." className="w-full px-3 py-2 mt-1 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                            {similarSearch.searching && <div className="absolute text-xs right-3 top-3 text-neutral-500">Mencari...</div>}
                        </div>
                        {similarSearch.results.length > 0 && (
                            <div className="mt-2 overflow-auto border max-h-72 rounded-xl border-neutral-200">
                                {similarSearch.results.map((hit) => {
                                    const isSelected = selectedIds.has(hit.id)
                                    const isSource = hit.id === productModelId
                                    const disabled = isSelected || isSource || isFull
                                    return (
                                        <button key={hit.id} type="button" disabled={disabled} onClick={() => addItem(hit)} className={`flex items-center w-full gap-3 p-2 text-left border-b border-neutral-100 last:border-b-0 ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-neutral-50"}`}>
                                            <div className="flex-shrink-0 w-12 h-12 overflow-hidden rounded-lg bg-neutral-100">
                                                {hit.image && <img src={hit.image} alt="" className="object-cover w-full h-full" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium truncate">{hit.name}</div>
                                                <div className="font-mono text-[11px] text-neutral-400">{hit.id}</div>
                                            </div>
                                            <div className="text-xs font-semibold">{isSource ? "Sumber" : isSelected ? "Dipilih" : isFull ? "Penuh" : "+ Tambah"}</div>
                                        </button>
                                    )
                                })}
                            </div>
                        )}
                    </div>

                    <div className="border border-neutral-200 rounded-xl">
                        <div className="flex items-center justify-between p-3 border-b border-neutral-200">
                            <div className="text-sm font-semibold">Produk serupa terpilih ({items.length}/{MAX_ITEMS})</div>
                            <div className="text-[11px] text-neutral-500">Urutan dari atas = urutan tampil.</div>
                        </div>
                        {items.length === 0 ? (
                            <div className="p-6 text-sm text-center text-neutral-500">Belum ada produk. Cari &amp; tambahkan dari atas.</div>
                        ) : (
                            <ul className="divide-y divide-neutral-100">
                                {items.map((it, idx) => (
                                    <li key={it.similar_product_model_id} className="flex items-center gap-3 p-2">
                                        <div className="w-6 font-mono text-xs text-center text-neutral-400">{idx + 1}</div>
                                        <div className="flex-shrink-0 w-12 h-12 overflow-hidden rounded-lg bg-neutral-100">
                                            {it.image && <img src={it.image} alt="" className="object-cover w-full h-full" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{it.name ?? "(Tanpa nama)"}</div>
                                            <div className="font-mono text-[11px] text-neutral-400 truncate">{it.similar_product_model_id}</div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button type="button" onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="px-2 py-1 text-xs rounded-lg bg-neutral-200 disabled:opacity-30" title="Naik">↑</button>
                                            <button type="button" onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} className="px-2 py-1 text-xs rounded-lg bg-neutral-200 disabled:opacity-30" title="Turun">↓</button>
                                            <button type="button" onClick={() => removeItem(idx)} className="px-2 py-1 text-xs text-white bg-red-600 rounded-lg" title="Hapus">✕</button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex items-center text-xs">
                <button type="submit" className="items-center px-6 py-3 text-white transition bg-brand-800 rounded-xl active:hover:scale-90">
                    <span>{isEdit ? "Perbarui" : "Simpan"}</span>
                </button>
            </div>
        </form>
    )
}
